import ListingFilterDefinition, {
  ListingFilterDefinitionOptionBadgeData,
  ListingFilterDefinitionOptionSelectData,
  ListingFilterDefinitionOptionSelectDataType,
  ListingFilterDefinitionOption,
} from "../../../../../../common/components/listing/filter/types/listing-filter-definition";
import orderOptimizerTranslationsHelper from "../../../../../../languages/order-optimizer-translations.helper";

import { OrderOptimizerRailyListingItemStatus } from "../../../../../order-optimizer/raily/listing/common/types/order-optimizer-raily-listing-item";
import orderOptimizerRailyListingTableRowHelper from "../../../../../order-optimizer/raily/listing/table/row/order-optimizer-raily-listing-table-row.helper";
import OrderDetailsManageDriverAssignmentOptitoolFilter, {
  OrderDetailsManageDriverAssignmentOptitoolStatusFilterValue,
  OrderDetailsManageDriverAssignmentOptitoolMaximumDelayFilter,
  OrderDetailsManageDriverAssignmentOptitoolMaximumHaltingTimeFilter,
  OrderDetailsManageDriverAssignmentOptitoolIgnoreDriverRoutesFilter,
  OrderDetailsManageDriverAssignmentOptitoolIgnoreTaxiFilter,
  OrderDetailsManageDriverAssignmentOptitoolIgnoreOnlyWithTaxiFilter,
  OrderDetailsManageDriverAssignmentOptitoolIgnoreCargoFilter,
  OrderDetailsManageDriverAssignmentOptitoolIgnoreOnlyWithCargoFilter,
  OrderDetailsManageDriverAssignmentOptitoolStatusFilter,
  OrderDetailsManageDriverAssignmentOptitoolOrderHumanIdFilter,
} from "./types/order-details-manage-driver-assignment-optitool-filter";
import OrderDetailsManageDriverAssignmentOptitoolFilterType from "./types/order-details-manage-driver-assignment-optitool-filter-type";

const orderDetailsManageDriverAssignmentOptitoolHelper = () => {
  const translations =
    orderOptimizerTranslationsHelper.getRailyListingTranslations();

  const getStatusLabel =
    orderOptimizerRailyListingTableRowHelper.getStatusLabel;

  const createBadgeData = (
    value: string,
    textTemplate: string,
    titleTemplate: string,
  ): ListingFilterDefinitionOptionBadgeData => {
    return {
      text: textTemplate.replace("#{query}", value),
      title: titleTemplate.replace("#{query}", value),
    };
  };

  const getSelectDataForOrderHumanId = (
    query: string,
  ): ListingFilterDefinitionOptionSelectData => {
    return {
      type: ListingFilterDefinitionOptionSelectDataType.NUMERIC_QUERY,
      options: [
        {
          label: translations.filters.search.orderHumanIdTemplateLabel.replace(
            "#{query}",
            query,
          ),
          value: Number.parseInt(query),
        },
      ],
    };
  };

  const getSelectDataForMaximumDelay = (
    openMaximumDelayModal: () => void,
  ): ListingFilterDefinitionOptionSelectData => {
    return {
      type: ListingFilterDefinitionOptionSelectDataType.CUSTOM,
      option: {
        label: translations.filters.search.maximumDelayTemplateLabel,
        onClick: () => openMaximumDelayModal(),
      },
    };
  };

  const getSelectDataForMaximumHaltingTime = (
    openMaximumHaltingTimeModal: () => void,
  ): ListingFilterDefinitionOptionSelectData => {
    return {
      type: ListingFilterDefinitionOptionSelectDataType.CUSTOM,
      option: {
        label: translations.filters.search.maximumHaltingTimeTemplateLabel,
        onClick: () => openMaximumHaltingTimeModal(),
      },
    };
  };

  const getSelectDataForIgnoreDriverRoutes =
    (): ListingFilterDefinitionOptionSelectData => {
      return {
        type: ListingFilterDefinitionOptionSelectDataType.DEFAULT,
        options: [
          {
            label: translations.filters.search.ignoreDriverRoutesTemplateLabel,
            value: true,
          },
        ],
      };
    };

  const getSelectDataForIgnoreTaxi = (
    query: string,
  ): ListingFilterDefinitionOptionSelectData => {
    return {
      type: ListingFilterDefinitionOptionSelectDataType.QUERY,
      options: [
        {
          label: translations.filters.search.ignoreTaxiTemplateLabel.replace(
            "#{query}",
            query,
          ),
          value: query,
        },
      ],
    };
  };

  const getSelectDataForIgnoreOnlyWithTaxi = (
    query: string,
  ): ListingFilterDefinitionOptionSelectData => {
    return {
      type: ListingFilterDefinitionOptionSelectDataType.QUERY,
      options: [
        {
          label:
            translations.filters.search.ignoreOnlyWithTaxiTemplateLabel.replace(
              "#{query}",
              query,
            ),
          value: query,
        },
      ],
    };
  };

  const getSelectDataForIgnoreCargo = (
    query: string,
  ): ListingFilterDefinitionOptionSelectData => {
    return {
      type: ListingFilterDefinitionOptionSelectDataType.QUERY,
      options: [
        {
          label: translations.filters.search.ignoreCargoTemplateLabel.replace(
            "#{query}",
            query,
          ),
          value: query,
        },
      ],
    };
  };

  const getSelectDataForIgnoreOnlyWithCargo = (
    query: string,
  ): ListingFilterDefinitionOptionSelectData => {
    return {
      type: ListingFilterDefinitionOptionSelectDataType.QUERY,
      options: [
        {
          label:
            translations.filters.search.ignoreOnlyWithCargoTemplateLabel.replace(
              "#{query}",
              query,
            ),
          value: query,
        },
      ],
    };
  };

  const getSelectDataForStatus = (
    status: OrderOptimizerRailyListingItemStatus,
  ): {
    label: string;
    value: OrderDetailsManageDriverAssignmentOptitoolStatusFilterValue;
  }[] => {
    return [
      {
        label: translations.filters.search.statusTemplateLabel.replace(
          "#{query}",
          getStatusLabel(status),
        ),
        value: {
          status: status,
          exclude: false,
        },
      },
      {
        label: translations.filters.search.excludeStatusTemplateLabel.replace(
          "#{query}",
          getStatusLabel(status),
        ),
        value: {
          status: status,
          exclude: true,
        },
      },
    ];
  };

  const getSelectDataForStatuses =
    (): ListingFilterDefinitionOptionSelectData => {
      return {
        type: ListingFilterDefinitionOptionSelectDataType.DEFAULT,
        options: Object.values(OrderOptimizerRailyListingItemStatus)
          .map(getSelectDataForStatus)
          .flat(),
      };
    };

  const getSelectDataByFilterType = (
    type: OrderDetailsManageDriverAssignmentOptitoolFilterType,
    query: string,
    openMaximumDelayModal: () => void,
    openMaximumHaltingTimeModal: () => void,
  ): ListingFilterDefinitionOptionSelectData => {
    const options: Record<
      OrderDetailsManageDriverAssignmentOptitoolFilterType,
      (query: string) => ListingFilterDefinitionOptionSelectData
    > = {
      [OrderDetailsManageDriverAssignmentOptitoolFilterType.ORDER_HUMAN_ID]:
        getSelectDataForOrderHumanId,
      [OrderDetailsManageDriverAssignmentOptitoolFilterType.MAXIMUM_DELAY]:
        () => getSelectDataForMaximumDelay(openMaximumDelayModal),
      [OrderDetailsManageDriverAssignmentOptitoolFilterType.MAXIMUM_HALTING_TIME]:
        () => getSelectDataForMaximumHaltingTime(openMaximumHaltingTimeModal),
      [OrderDetailsManageDriverAssignmentOptitoolFilterType.IGNORE_DRIVER_ROUTES]:
        getSelectDataForIgnoreDriverRoutes,
      [OrderDetailsManageDriverAssignmentOptitoolFilterType.IGNORE_TAXI]:
        getSelectDataForIgnoreTaxi,
      [OrderDetailsManageDriverAssignmentOptitoolFilterType.IGNORE_ONLY_WITH_TAXI]:
        getSelectDataForIgnoreOnlyWithTaxi,
      [OrderDetailsManageDriverAssignmentOptitoolFilterType.IGNORE_CARGO]:
        getSelectDataForIgnoreCargo,
      [OrderDetailsManageDriverAssignmentOptitoolFilterType.IGNORE_ONLY_WITH_CARGO]:
        getSelectDataForIgnoreOnlyWithCargo,
      [OrderDetailsManageDriverAssignmentOptitoolFilterType.STATUS]:
        getSelectDataForStatuses,
    };

    return options[type](query);
  };

  const getBadgeForOrderHumanId = (
    filterValue: OrderDetailsManageDriverAssignmentOptitoolOrderHumanIdFilter["value"],
  ): ListingFilterDefinitionOptionBadgeData => {
    return createBadgeData(
      String(filterValue),
      translations.filters.orderHumanIdLabel,
      translations.filters.orderHumanIdTitle,
    );
  };

  const getBadgeForMaximumDelay = (
    filterValue: OrderDetailsManageDriverAssignmentOptitoolMaximumDelayFilter["value"],
  ): ListingFilterDefinitionOptionBadgeData => {
    return createBadgeData(
      String(filterValue),
      translations.filters.maximumDelayLabel,
      translations.filters.maximumDelayTitle,
    );
  };

  const getBadgeForMaximumHaltingTime = (
    filterValue: OrderDetailsManageDriverAssignmentOptitoolMaximumHaltingTimeFilter["value"],
  ): ListingFilterDefinitionOptionBadgeData => {
    return createBadgeData(
      String(filterValue),
      translations.filters.maximumHaltingTimeLabel,
      translations.filters.maximumHaltingTimeTitle,
    );
  };

  const getBadgeForIgnoreDriverRoutes = (
    filterValue: OrderDetailsManageDriverAssignmentOptitoolIgnoreDriverRoutesFilter["value"],
  ): ListingFilterDefinitionOptionBadgeData => {
    return createBadgeData(
      String(filterValue),
      translations.filters.ignoreDriverRoutesLabel,
      translations.filters.ignoreDriverRoutesTitle,
    );
  };

  const getBadgeForIgnoreTaxi = (
    filterValue: OrderDetailsManageDriverAssignmentOptitoolIgnoreTaxiFilter["value"],
  ): ListingFilterDefinitionOptionBadgeData => {
    return createBadgeData(
      filterValue,
      translations.filters.ignoreTaxiLabel,
      translations.filters.ignoreTaxiTitle,
    );
  };

  const getBadgeForIgnoreOnlyWithTaxi = (
    filterValue: OrderDetailsManageDriverAssignmentOptitoolIgnoreOnlyWithTaxiFilter["value"],
  ): ListingFilterDefinitionOptionBadgeData => {
    return createBadgeData(
      filterValue,
      translations.filters.ignoreOnlyWithTaxiLabel,
      translations.filters.ignoreOnlyWithTaxiTitle,
    );
  };

  const getBadgeForIgnoreCargo = (
    filterValue: OrderDetailsManageDriverAssignmentOptitoolIgnoreCargoFilter["value"],
  ): ListingFilterDefinitionOptionBadgeData => {
    return createBadgeData(
      filterValue,
      translations.filters.ignoreCargoLabel,
      translations.filters.ignoreCargoTitle,
    );
  };

  const getBadgeForIgnoreOnlyWithCargo = (
    filterValue: OrderDetailsManageDriverAssignmentOptitoolIgnoreOnlyWithCargoFilter["value"],
  ): ListingFilterDefinitionOptionBadgeData => {
    return createBadgeData(
      filterValue,
      translations.filters.ignoreOnlyWithCargoLabel,
      translations.filters.ignoreOnlyWithCargoTitle,
    );
  };

  const getBadgeDataForStatus = (
    filterValue: OrderDetailsManageDriverAssignmentOptitoolStatusFilter["value"],
  ) => {
    if (filterValue.exclude) {
      return createBadgeData(
        getStatusLabel(filterValue.status),
        translations.filters.excludeStatusLabel,
        translations.filters.excludeStatusTtitle,
      );
    } else {
      return createBadgeData(
        getStatusLabel(filterValue.status),
        translations.filters.statusLabel,
        translations.filters.statusTitle,
      );
    }
  };

  const getBadgeDataByFilterType = (
    type: OrderDetailsManageDriverAssignmentOptitoolFilterType,
    filterValue: OrderDetailsManageDriverAssignmentOptitoolFilter["value"],
  ): ListingFilterDefinitionOptionBadgeData => {
    const options: Record<
      OrderDetailsManageDriverAssignmentOptitoolFilterType,
      (
        filterValue: OrderDetailsManageDriverAssignmentOptitoolFilter["value"],
      ) => ListingFilterDefinitionOptionBadgeData
    > = {
      [OrderDetailsManageDriverAssignmentOptitoolFilterType.ORDER_HUMAN_ID]:
        getBadgeForOrderHumanId,
      [OrderDetailsManageDriverAssignmentOptitoolFilterType.MAXIMUM_DELAY]:
        getBadgeForMaximumDelay,
      [OrderDetailsManageDriverAssignmentOptitoolFilterType.MAXIMUM_HALTING_TIME]:
        getBadgeForMaximumHaltingTime,
      [OrderDetailsManageDriverAssignmentOptitoolFilterType.IGNORE_DRIVER_ROUTES]:
        getBadgeForIgnoreDriverRoutes,
      [OrderDetailsManageDriverAssignmentOptitoolFilterType.IGNORE_TAXI]:
        getBadgeForIgnoreTaxi,
      [OrderDetailsManageDriverAssignmentOptitoolFilterType.IGNORE_ONLY_WITH_TAXI]:
        getBadgeForIgnoreOnlyWithTaxi,
      [OrderDetailsManageDriverAssignmentOptitoolFilterType.IGNORE_CARGO]:
        getBadgeForIgnoreCargo,
      [OrderDetailsManageDriverAssignmentOptitoolFilterType.IGNORE_ONLY_WITH_CARGO]:
        getBadgeForIgnoreOnlyWithCargo,
      [OrderDetailsManageDriverAssignmentOptitoolFilterType.STATUS]:
        getBadgeDataForStatus,
    };

    return options[type](filterValue);
  };

  const getMultiApplicableFlagByType = (
    type: OrderDetailsManageDriverAssignmentOptitoolFilterType,
  ): boolean => {
    const options: Record<
      OrderDetailsManageDriverAssignmentOptitoolFilterType,
      boolean
    > = {
      IGNORE_CARGO: true,
      IGNORE_DRIVER_ROUTES: false,
      IGNORE_ONLY_WITH_CARGO: true,
      IGNORE_ONLY_WITH_TAXI: true,
      IGNORE_TAXI: true,
      MAXIMUM_DELAY: false,
      MAXIMUM_HALTING_TIME: false,
      ORDER_HUMAN_ID: false,
      STATUS: false,
    };

    return options[type];
  };

  const getFilterDefinitionOptionByType = (
    type: OrderDetailsManageDriverAssignmentOptitoolFilterType,
    openMaximumDelayModal: () => void,
    openMaximumHaltingTimeModal: () => void,
  ): ListingFilterDefinitionOption<OrderDetailsManageDriverAssignmentOptitoolFilter> => {
    return {
      filterType: type,
      isMultiApplicable: getMultiApplicableFlagByType(type),
      getSelectData: (query) =>
        getSelectDataByFilterType(
          type,
          query,
          openMaximumDelayModal,
          openMaximumHaltingTimeModal,
        ),
      getBadgeData: (value) => getBadgeDataByFilterType(type, value),
    };
  };

  const getFilterDefinitionOptions = (
    openMaximumDelayModal: () => void,
    openMaximumHaltingTimeModal: () => void,
  ): ListingFilterDefinitionOption<OrderDetailsManageDriverAssignmentOptitoolFilter>[] => {
    return Object.values(
      OrderDetailsManageDriverAssignmentOptitoolFilterType,
    ).map((type) =>
      getFilterDefinitionOptionByType(
        type,
        openMaximumDelayModal,
        openMaximumHaltingTimeModal,
      ),
    );
  };

  const getFilterDefinition = (
    openMaximumDelayModal: () => void,
    openMaximumHaltingTimeModal: () => void,
  ): ListingFilterDefinition<OrderDetailsManageDriverAssignmentOptitoolFilter> => {
    return {
      options: getFilterDefinitionOptions(
        openMaximumDelayModal,
        openMaximumHaltingTimeModal,
      ),
    };
  };

  return {
    getFilterDefinition,
  };
};

export default orderDetailsManageDriverAssignmentOptitoolHelper;
